import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';

import type { EsnSegmentedOption } from '../../../shared/types/forms';
import type { EsnFeedbackPayload } from '../../../shared/types/support';
import { ButtonComponent } from '../../atoms/button/button.component';
import { SegmentedComponent } from '../../molecules/segmented/segmented.component';
import { TextFieldComponent } from '../../molecules/text-field/text-field.component';

let nextId = 0;

/**
 * The Help Center feedback form: pick what it is about, give it a subject and the details,
 * and send it to the support team. Send stays disabled until kind, subject and details are filled.
 */
@Component({
  selector: 'esn-feedback-form',
  imports: [ButtonComponent, SegmentedComponent, TextFieldComponent],
  templateUrl: './feedback-form.component.html',
  styleUrl: './feedback-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackFormComponent {
  readonly heading = input('');
  readonly description = input('');

  /**
   * The feedback kinds (Figma: Problem / Idea / Question) and the chosen one, `[(kind)]`.
   */
  readonly kinds = input<EsnSegmentedOption[]>([]);
  readonly kind = model<string | null>(null);
  readonly kindLabel = input('');

  readonly subject = model('');
  readonly subjectLabel = input('');
  readonly subjectPlaceholder = input('');

  readonly details = model('');
  readonly detailsLabel = input('');
  readonly detailsPlaceholder = input('');
  readonly detailsHint = input('');

  /**
   * Read-only address the reply goes to; the row hides when empty.
   */
  readonly replyTo = input('');
  readonly replyToLabel = input('');

  readonly clearLabel = input('');
  readonly submitLabel = input('');

  /**
   * Locks the whole form, e.g. while the feedback is being sent.
   */
  readonly disabled = input(false);

  readonly submit = output<EsnFeedbackPayload>();
  readonly cleared = output<void>();

  protected readonly headingId = `esn-feedback-form-${nextId++}`;

  protected readonly ready = computed(() => !this.disabled() && !!this.kind() && !!this.subject().trim() && !!this.details().trim());
  protected readonly dirty = computed(() => !this.disabled() && (!!this.subject() || !!this.details()));

  protected handleSubmit(event: Event): void {
    // Keep the native submit event from bubbling to the host, where it would collide with `(submit)`.
    event.preventDefault();
    event.stopPropagation();
    const kind = this.kind();
    if (!this.ready() || !kind) return;
    this.submit.emit({ kind, subject: this.subject().trim(), details: this.details().trim() });
  }

  protected clear(): void {
    this.subject.set('');
    this.details.set('');
    this.cleared.emit();
  }
}
